/**
 * This hook manages the creating or updating of route templates, trip templates,
 * and multiload templates.
 *
 * This is a multi-step process that triggers requests in order of:
 * RouteTemplate -> RouteTripTemplate[] -> RouteMultiloadTemplate[]
 */

import { useCallback, useState } from 'react';

import {
  RouteMultiloadTemplateRead,
  RouteTripTemplateRead,
  useRoutesRouteTemplatesCreateMutation,
  useRoutesRouteTemplatesMultiloadCreateMutation,
  useRoutesRouteTemplatesMultiloadDestroyMutation,
  useRoutesRouteTemplatesMultiloadPartialUpdateMutation,
  useRoutesRouteTemplatesPartialUpdateMutation,
  useRoutesRouteTemplatesTripTemplatesCreateMutation,
  useRoutesRouteTemplatesTripTemplatesDestroyMutation,
  useRoutesRouteTemplatesTripTemplatesPartialUpdateMutation,
} from 'src/common/external/bambi-api/routesApi';

import { orderDays } from '../management/DayOfTheWeekCell';
import { normalizeData } from './normalizeData';
import { RouteTemplateFormValues } from './types';

export function useUpsertRouteTemplate() {
  const [isLoading, setIsLoading] = useState(false);
  const [createRouteTemplate] = useRoutesRouteTemplatesCreateMutation();
  const [createRouteTripTemplate] =
    useRoutesRouteTemplatesTripTemplatesCreateMutation();
  const [createRouteMultiloadTemplate] =
    useRoutesRouteTemplatesMultiloadCreateMutation();

  const [patchRouteTemplate] = useRoutesRouteTemplatesPartialUpdateMutation();
  const [patchRouteTripTemplate] =
    useRoutesRouteTemplatesTripTemplatesPartialUpdateMutation();
  const [patchRouteMultiloadTemplate] =
    useRoutesRouteTemplatesMultiloadPartialUpdateMutation();

  const [deleteRouteTripTemplate] =
    useRoutesRouteTemplatesTripTemplatesDestroyMutation();

  const [deleteRouteMultiloadTemplate] =
    useRoutesRouteTemplatesMultiloadDestroyMutation();

  const triggerCreateMultiloadTripTemplate = useCallback(
    async (
      data: RouteMultiloadTemplateRead,
      resolvedTripTemplates: {
        tripTemplate: RouteTripTemplateRead;
        temporaryPickupLocationDetailId: string;
        temporaryDropoffLocationDetailId: string;
      }[],
      routeTemplateId: string
    ) => {
      const tripLocationDetailTemporaryIdMap = resolvedTripTemplates.reduce(
        (acc, tripTemplate) => {
          acc[tripTemplate.temporaryPickupLocationDetailId] =
            tripTemplate.tripTemplate.pickup.id;
          acc[tripTemplate.temporaryDropoffLocationDetailId] =
            tripTemplate.tripTemplate.dropoff.id;
          return acc;
        },
        {} as Record<string, string>
      );

      await createRouteMultiloadTemplate({
        routeTemplateId,
        routeMultiloadTemplate: {
          template_trip_location_details:
            data.template_trip_location_details.map((tripTemplate, index) => {
              return {
                order: 1 + index,
                trip_location_detail_id:
                  tripLocationDetailTemporaryIdMap[
                    tripTemplate.trip_location_detail_id
                  ],
              };
            }),
        },
      }).unwrap();
    },
    [createRouteMultiloadTemplate]
  );

  const triggerCreateRouteTemplate = useCallback(
    async (data: RouteTemplateFormValues) => {
      const { sortedMatchers, startTime, endTime, rangeStart, rangeEnd } =
        normalizeData(data);
      setIsLoading(true);

      try {
        // Create route template
        const routeTemplate = await createRouteTemplate({
          routeTemplate: {
            name: data.name,
            notes: data.notes,
            start_time: startTime,
            end_time: endTime,
            range_start: rangeStart,
            range_end: rangeEnd,
            days_of_week: orderDays(data.days_of_week),
            default_driver_id:
              data.default_driver?.value ?? 'invalid-or-missing-id',
            allow_rbr_trips: data.allow_rbr_trips,
          },
        }).unwrap();

        // Create route trip templates
        const routeTripTemplates = await Promise.all(
          sortedMatchers.map(async (matcher) => {
            const tripTemplate = await createRouteTripTemplate({
              routeTemplateId: routeTemplate.id,
              routeTripTemplate: {
                dropoff: matcher.dropoff,
                pickup: matcher.pickup,
                passenger_id: matcher.passenger_id,
                scheduled_pickup_time: matcher.scheduled_pickup_time,
                space_type: matcher.space_type,
                num_accompanying_passengers:
                  matcher.num_accompanying_passengers,
              },
            }).unwrap();

            return {
              tripTemplate,
              // Need the temporary ids to resolve the trip location detail ids in
              // the multiload trip templates
              temporaryPickupLocationDetailId: matcher.pickup.id,
              temporaryDropoffLocationDetailId: matcher.dropoff.id,
            };
          })
        );

        // Create multiload trip templates
        await Promise.all(
          data.multiload_trip_templates.map((multiloadTripTemplate) => {
            return triggerCreateMultiloadTripTemplate(
              multiloadTripTemplate,
              routeTripTemplates,
              routeTemplate.id
            );
          })
        );
      } finally {
        setIsLoading(false);
      }
    },
    [
      createRouteTemplate,
      createRouteTripTemplate,
      triggerCreateMultiloadTripTemplate,
    ]
  );

  const triggerUpdateRouteTemplate = useCallback(
    async (
      data: RouteTemplateFormValues,
      currentTripTemplateIds: string[],
      currentMultiloadTripTemplateIds: string[]
    ) => {
      const { sortedMatchers, startTime, endTime, rangeStart, rangeEnd } =
        normalizeData(data);

      setIsLoading(true);

      try {
        // Find any deleted trip templates
        const deletedTripTemplates = currentTripTemplateIds.filter(
          (id) => !sortedMatchers.some((matcher) => matcher.id === id)
        );

        // Delete any deleted trip templates
        await Promise.all(
          deletedTripTemplates.map((id) => {
            if (!id) {
              return Promise.resolve();
            }

            return deleteRouteTripTemplate({
              routeTemplateId: data.id,
              id,
            }).unwrap();
          })
        );

        // Find any deleted multiload trip templates
        const deletedMultiloadTripTemplates =
          currentMultiloadTripTemplateIds.filter(
            (id) =>
              !data.multiload_trip_templates.some(
                (template) => template.id === id
              )
          );

        // Delete any deleted multiload trip templates
        await Promise.all(
          deletedMultiloadTripTemplates.map((id) => {
            return deleteRouteMultiloadTemplate({
              routeTemplateId: data.id,
              id,
            }).unwrap();
          })
        );

        // Update route template
        await patchRouteTemplate({
          id: data.id,
          patchedRouteTemplate: {
            name: data.name,
            notes: data.notes,
            start_time: startTime,
            end_time: endTime,
            range_start: rangeStart,
            range_end: rangeEnd,
            days_of_week: orderDays(data.days_of_week),
            default_driver_id:
              data.default_driver?.value ?? 'invalid-or-missing-id',
            allow_rbr_trips: data.allow_rbr_trips,
          },
        }).unwrap();

        // Update or create route trip templates
        const routeTripTemplates = await Promise.all(
          sortedMatchers.map(async (matcher) => {
            if (matcher.id) {
              const tripTemplate = await patchRouteTripTemplate({
                id: matcher.id!,
                routeTemplateId: data.id,
                patchedRouteTripTemplate: {
                  dropoff: matcher.dropoff,
                  pickup: matcher.pickup,
                  passenger_id: matcher.passenger_id,
                  scheduled_pickup_time: matcher.scheduled_pickup_time,
                  space_type: matcher.space_type,
                  num_accompanying_passengers:
                    matcher.num_accompanying_passengers,
                },
              }).unwrap();

              return {
                tripTemplate,
                temporaryPickupLocationDetailId: matcher.pickup.id,
                temporaryDropoffLocationDetailId: matcher.dropoff.id,
              };
            } else {
              const tripTemplate = await createRouteTripTemplate({
                routeTemplateId: data.id,
                routeTripTemplate: {
                  dropoff: matcher.dropoff,
                  pickup: matcher.pickup,
                  passenger_id: matcher.passenger_id,
                  scheduled_pickup_time: matcher.scheduled_pickup_time,
                  space_type: matcher.space_type,
                  num_accompanying_passengers:
                    matcher.num_accompanying_passengers,
                },
              }).unwrap();

              return {
                tripTemplate,
                temporaryPickupLocationDetailId: matcher.pickup.id,
                temporaryDropoffLocationDetailId: matcher.dropoff.id,
              };
            }
          })
        );

        // Update multiload trip templates
        await Promise.all(
          data.multiload_trip_templates.map((multiloadTripTemplate) => {
            if (multiloadTripTemplate.id) {
              return patchRouteMultiloadTemplate({
                routeTemplateId: data.id,
                id: multiloadTripTemplate.id,
                patchedRouteMultiloadTemplate: {
                  template_trip_location_details:
                    multiloadTripTemplate.template_trip_location_details.map(
                      (tripTemplate) => {
                        return {
                          order: tripTemplate.order,
                          trip_location_detail_id:
                            tripTemplate.trip_location_detail_id,
                        };
                      }
                    ),
                },
              }).unwrap();
            } else {
              return triggerCreateMultiloadTripTemplate(
                multiloadTripTemplate,
                routeTripTemplates,
                data.id
              );
            }
          })
        );
      } finally {
        setIsLoading(false);
      }
    },
    [
      patchRouteTemplate,
      deleteRouteTripTemplate,
      deleteRouteMultiloadTemplate,
      patchRouteTripTemplate,
      createRouteTripTemplate,
      patchRouteMultiloadTemplate,
      triggerCreateMultiloadTripTemplate,
    ]
  );

  return {
    createRouteTemplate: triggerCreateRouteTemplate,
    updateRouteTemplate: triggerUpdateRouteTemplate,
    isLoading,
  };
}
